import { useRef, type ReactNode } from 'react'
import type { TmsInvoiceRowDocuments, TmsPodDocumentState } from '@numeo/types-main-web-app'
import { Badge, type BadgeType } from '@/components/ds2'
import { formatUsd } from './format'

/** One document slot: on file and good (ok), on file but needing a look (warn), missing (bad), or not required here (na). */
type DocumentSlot = 'ok' | 'warn' | 'bad' | 'na'

interface LoadRowDocuments {
    rc: DocumentSlot
    bol: DocumentSlot
    pod: DocumentSlot
}

/** A slot that can be acted on: a missing or failed document is a button that does its one fix (the tooltip names it). `onFile` opens the
 * file picker and hands the chosen file over; `onClick` does a fix that is not a file. */
export interface SlotAction {
    label: string
    onClick?: () => void
    onFile?: (file: File) => void
}

type LoadRowSlotActions = Partial<Record<keyof LoadRowDocuments, SlotAction>>

const POD_SLOT: Record<TmsPodDocumentState, DocumentSlot> = { accepted: 'ok', on_file: 'warn', none: 'bad' }

/** The three fixed slots of a load's documents. A load in transit shows dashes for what is not due yet, never a missing one. */
export const documentSlotsOf = (documents: TmsInvoiceRowDocuments | undefined, inTransit = false): LoadRowDocuments => {
    if (!documents) return { rc: 'na', bol: 'na', pod: 'na' }
    return {
        rc: documents.rate_con ? 'ok' : inTransit ? 'na' : 'bad',
        bol: documents.bol ? 'ok' : 'na',
        pod: inTransit ? 'na' : (POD_SLOT[documents.pod] ?? 'bad'),
    }
}

/** A slot's ds2 Badge: soft status tones; a slot not required here is the neutral tone with muted text. */
const SLOT_TYPE: Record<DocumentSlot, BadgeType> = { ok: 'success', warn: 'warning', bad: 'danger', na: 'default' }

const SLOT_TEXT: Record<DocumentSlot, string> = { ok: '', warn: '', bad: '', na: 'text-foreground-muted' }

const SLOT_MARK: Record<DocumentSlot, string> = { ok: '✓', warn: '!', bad: '✕', na: '' }

const SLOT_WORDS: Record<DocumentSlot, string> = { ok: 'on file', warn: 'needs review', bad: 'missing', na: 'not required' }

/** The fixed slot width keeps the three document columns aligned across rows. */
const SLOT_BASE = 'h-5 w-9 shrink-0 justify-center'

/** The file types a document slot takes: the same as the chat's upload control. */
const SLOT_ACCEPT = 'application/pdf,image/jpeg,image/png'

/**
 * One document chip. A slot that is not on file and has an action is a button (hover ring, tooltip): a file action opens the picker, a
 * click action runs its fix. Otherwise a static chip.
 */
const Slot = ({ label, slot, action }: { label: string; slot: DocumentSlot; action?: SlotAction }) => {
    const picker = useRef<HTMLInputElement>(null)
    const badge = (
        <Badge
            variant="soft"
            type={SLOT_TYPE[slot]}
            size="sm"
            label={label}
            suffix={SLOT_MARK[slot] ? <span aria-hidden>{SLOT_MARK[slot]}</span> : undefined}
            className={`${SLOT_BASE} ${SLOT_TEXT[slot]}`}
        />
    )
    if (action && slot !== 'ok' && (action.onFile || action.onClick))
        return (
            <>
                <button
                    type="button"
                    title={action.label}
                    aria-label={`${label}: ${SLOT_WORDS[slot]}. ${action.label}`}
                    onClick={() => (action.onFile ? picker.current?.click() : action.onClick?.())}
                    className="cursor-pointer rounded-xl transition-shadow duration-150 hover:ring-2 hover:ring-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    {badge}
                </button>
                {action.onFile && (
                    <input
                        ref={picker}
                        type="file"
                        accept={SLOT_ACCEPT}
                        aria-label={action.label}
                        className="hidden"
                        onChange={(event) => {
                            const file = event.target.files?.[0]
                            event.target.value = ''
                            if (file) action.onFile?.(file)
                        }}
                    />
                )}
            </>
        )
    return (
        <span aria-label={`${label}: ${SLOT_WORDS[slot]}`} className="inline-flex">
            {badge}
        </span>
    )
}

/**
 * The load number in its fixed column, on one line. Several loads on one row read as the first number and "+N"; the full list is the tooltip.
 */
const NumberCell = ({ number }: { number: string }) => {
    const [first, ...rest] = number.split(', ').filter(Boolean)
    if (!first) return <span className="min-w-0 truncate text-foreground-muted">No load #</span>
    return (
        <span className="min-w-0 truncate font-medium tabular-nums" title={number}>
            #{first ?? number}
            {rest.length > 0 && <span className="text-foreground-muted"> +{rest.length}</span>}
        </span>
    )
}

/** A load with no rate (null or $0) reads "$0 · no rate" in the warning colour, so it is never taken for a real $0 invoice. */
const NO_RATE = '$0 · no rate'

interface LoadRowProps {
    /** The load's number, without the `#`. Kept on one line. */
    number: string
    /** The broker and the lane, or any short identity line. Cut to one line; a plain string is its own tooltip. */
    title: ReactNode
    /** The tooltip of a title that is not a plain string. */
    titleTooltip?: string
    /** One line of state under the title, e.g. "Delivered Oct 28", "Needs documents", or a written state. */
    state: ReactNode
    /** The three document slots. Omitted (a TONU, a load that needs none here): all three show dashes and none is required. */
    documents?: LoadRowDocuments
    /** The amount: null or 0 reads "no rate"; undefined (no amount to say) leaves the cell empty. */
    amount?: number | null
    /** The row's one action, in the fixed-width action column. Nothing (an Amazon-billed load) leaves the column empty. */
    action?: ReactNode
    /** A leading control (a checkbox), in its own fixed column. */
    lead?: ReactNode
    /** The row is an older state of a load a later answer shows again: dimmed, and says so. */
    dimmed?: boolean
    /** The broker is on the red-flag list: a chip beside the title. */
    redFlag?: boolean
    /** Full-width content under the row: the approval, a revision, a reason. */
    details?: ReactNode
    /** A narrow host (the side panel): the row takes two lines, the number, broker and amount above, the state, slots and action below. */
    dense?: boolean
    /** The actions the document slots offer: a missing or failed chip is a button that does its fix. */
    slots?: LoadRowSlotActions
}

const amountWords = (amount: number | null | undefined): string => {
    if (amount === undefined) return ''
    if (amount === null || amount === 0) return NO_RATE
    return formatUsd(amount)
}

/**
 * One load line everywhere a load is shown (design §3): number, broker and lane with one state line, RC, BOL and POD in three fixed
 * slots, the amount right-aligned and one action in a fixed-width column. Columns are the same on every card, so a slot never moves
 * when a document arrives. A missing document is a button that does its fix. Under the narrow width (`dense`) the row takes two lines.
 */
export const LoadRow = ({
    number,
    title,
    titleTooltip,
    state,
    documents,
    amount,
    action,
    lead,
    dimmed = false,
    redFlag = false,
    details,
    dense = false,
    slots,
}: LoadRowProps) => {
    const docs = documents ?? { rc: 'na' as const, bol: 'na' as const, pod: 'na' as const }
    const noRate = amount === null || amount === 0
    const chips = (
        <span className="flex shrink-0 items-center gap-1">
            <Slot label="RC" slot={docs.rc} action={slots?.rc} />
            <Slot label="BOL" slot={docs.bol} action={slots?.bol} />
            <Slot label="POD" slot={docs.pod} action={slots?.pod} />
        </span>
    )
    const redFlagChip = redFlag && (
        <span title="Red-flagged broker: it can't be sent to factoring" className="inline-flex shrink-0">
            <Badge variant="soft" type="danger" size="sm" label="Red-flag" />
        </span>
    )
    const titleBlock = (
        <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate" title={titleTooltip ?? (typeof title === 'string' ? title : undefined)}>
                {title}
            </span>
            {redFlagChip}
        </span>
    )
    const stateLine = (
        <span className="block min-w-0 truncate text-foreground-muted">
            {state}
            {dimmed && <span className="text-warning"> · Updated below ↓</span>}
        </span>
    )

    if (dense)
        return (
            <div data-testid="load-row" data-load={number} className={`flex flex-col gap-1.5 py-2 text-xs ${dimmed ? 'opacity-60' : ''}`}>
                <div className="flex min-w-0 items-center gap-2">
                    {lead}
                    <NumberCell number={number} />
                    <span className="min-w-0 flex-1">{titleBlock}</span>
                    <span className={`shrink-0 whitespace-nowrap text-right tabular-nums ${noRate ? 'text-warning' : ''}`}>{amountWords(amount)}</span>
                </div>
                <div className="flex min-w-0 items-center gap-2">
                    <span className="min-w-0 flex-1">{stateLine}</span>
                    {chips}
                    {action && <span className="flex w-36 shrink-0 justify-end">{action}</span>}
                </div>
                {details && <div className="w-full">{details}</div>}
            </div>
        )

    return (
        <div
            data-testid="load-row"
            data-load={number}
            className={`flex flex-col gap-1 border-t border-border py-2 text-xs first:border-t-0 ${dimmed ? 'opacity-60' : ''}`}
        >
            {/* Fixed columns from the small width up: lead · number · broker and state · RC/BOL/POD · amount · action (9.5rem, so the longest label fits). */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 sm:grid sm:grid-cols-[1.25rem_6.25rem_minmax(0,1fr)_6.5rem_6.5rem_9.5rem] sm:gap-2">
                <span className="flex w-5 shrink-0 items-center justify-center">{lead}</span>
                <NumberCell number={number} />
                <span className="min-w-0 flex-1 sm:flex-none">
                    {titleBlock}
                    {stateLine}
                </span>
                <span className="order-3 flex items-center gap-1 sm:order-none">{chips}</span>
                <span className={`order-2 text-right tabular-nums sm:order-none ${noRate ? 'text-warning' : ''}`}>{amountWords(amount)}</span>
                <span className="order-4 ml-auto flex w-full justify-end sm:order-none sm:ml-0 sm:w-auto">{action}</span>
            </div>
            {details && <div className="w-full">{details}</div>}
        </div>
    )
}
